import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionDecor } from "@/components/SectionDecor";
import shingleImg from "@/assets/service-heroes/roof-replacement.jpg.asset.json";
import luxuryImg from "@/assets/mckay/roof-replacement.webp.asset.json";
import tileImg from "@/assets/gallery/6940e3a896e7535d5af6346b.webp.asset.json";
import tpoImg from "@/assets/blog/blog-1.jpg.asset.json";
import metalImg from "@/assets/service-heroes/residential-roofing.jpg.asset.json";
import sheetMetalImg from "@/assets/blog/blog-3.jpg.asset.json";
import torchImg from "@/assets/blog/blog-2.jpg.asset.json";

export type Material = {
  name: string;
  blurb: string;
  bestFor: string;
  href?: string;
  image?: string;
  alt?: string;
};

export const roofingMaterials: Material[] = [

  {
    name: "Asphalt, Wood, Metal & Shingle",
    blurb:
      "The everyday workhorses. Three-tab and architectural asphalt shingles cover most homes we touch, and we still hand-cut wood shake repairs and set metal panels where a roof calls for it. Good value, fast turnaround, easy to service years down the road.",
    bestFor: "Most tract and custom homes across Southern California",
    href: "/services/asphalt-shingle-roofing",
    image: shingleImg.url,
    alt: "Architectural asphalt shingle roof installed on a Southern California home",
  },
  {
    name: "Luxury Shingles",
    blurb:
      "Heavier, thicker profiles that mimic slate or hand-split shake without the weight or the price. They carry longer manufacturer coverage, hold up better in wind, and give a plain roofline real depth from the street.",
    bestFor: "Homeowners who want tile presence with shingle simplicity",
    href: "/services/luxury-shingle-roofing",
    image: luxuryImg.url,
    alt: "Luxury shingle roof with a thick dimensional profile",
  },
  {
    name: "Concrete & Clay Tile Roofs",
    blurb:
      "Classic Southern California. Tile itself lasts decades, but the underlayment beneath it does not. We lift and stack, replace the paper and metal, then reset or convert depending on what makes sense for the house.",
    bestFor: "Spanish, Mediterranean and mission-style homes",
    href: "/services/tile-roofing",
    image: tileImg.url,
    alt: "Concrete and clay tile roof on a Mediterranean-style home",
  },
  {
    name: "PVC & TPO",
    blurb:
      "Single-ply membrane for low-slope and flat roofs. Heat-welded seams instead of adhesives, a bright reflective surface that keeps attic temperatures down, and a clean surface for solar to sit on later.",
    bestFor: "Flat residential decks and commercial buildings",
    href: "/services/tpo-roofing",
    image: tpoImg.url,
    alt: "White single-ply TPO membrane roof with heat-welded seams",
  },
  {
    name: "Standing Seam Metal",
    blurb:
      "Concealed-fastener panels with raised seams, so there are no exposed screws to back out and leak. It sheds water and debris fast, handles coastal weather, and pairs well with clamp-mounted solar that never penetrates the roof.",
    bestFor: "Modern builds, additions, patio and carport roofs",
    href: "/services/standing-seam-metal-roofing",
    image: metalImg.url,
    alt: "Standing seam metal roof with concealed fasteners",
  },
  {
    name: "Architectural Sheet Metal",
    blurb:
      "The details most crews rush. Custom-bent flashing, coping, gravel stops, cricket and chimney pans, scuppers and gutters fabricated to fit the actual roof instead of a stock length from the supply house.",
    bestFor: "Tricky transitions, parapets and chimney details",
    href: "/services/architectural-sheet-metal",
    image: sheetMetalImg.url,
    alt: "Custom-bent architectural sheet metal flashing and coping",
  },
  {
    name: "Hot & Torch",
    blurb:
      "Built-up hot mop and torch-down modified bitumen. Multiple reinforced plies fused into one waterproof mass, which is still the toughest answer for a flat roof that ponds water or takes regular foot traffic.",
    bestFor: "Flat roofs, decks and roofs with heavy service traffic",
    href: "/services/torch-down-roofing",
    image: torchImg.url,
    alt: "Torch-down modified bitumen flat roof being installed",
  },
];

/** Compact grid used on the homepage. */
export function RoofingMaterials({
  heading = "Types of Roofing Materials",
  showCta = true,
}: {
  heading?: string;
  showCta?: boolean;
}) {
  return (
    <section className="relative overflow-hidden bg-muted/40">
      <SectionDecor variant="roof" side="right" tone="primary" opacity={0.05} size={680} />
      <div className="relative z-10 mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">Materials we install</div>
          <h2 className="mt-3 font-serif text-4xl font-bold text-primary sm:text-5xl text-balance">{heading}</h2>
          <p className="mt-4 text-lg text-muted-foreground">
            At McKay Roofing &amp; Solar we stock and install the materials that actually hold up in Southern
            California weather. Our specialists walk the roof with you, look at the slope, the framing and the look you
            are after, then recommend the system that protects the house and suits the home.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {roofingMaterials.map((m) => (
            <div
              key={m.name}
              className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-secondary/40 hover:shadow-elegant"
            >
              {m.image ? (
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={m.image}
                    alt={m.alt ?? m.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent" />
                  <h3 className="absolute inset-x-5 bottom-4 font-serif text-xl font-bold text-primary-foreground drop-shadow">
                    {m.name}
                  </h3>
                </div>
              ) : (
                <h3 className="px-6 pt-6 font-serif text-xl font-bold text-primary">{m.name}</h3>
              )}
              <div className="flex flex-1 flex-col p-6">
                <p className="flex-1 text-sm leading-relaxed text-muted-foreground">{m.blurb}</p>
                <div className="mt-4 flex gap-2 border-t border-border pt-4 text-xs text-foreground">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                  <span>
                    <span className="font-semibold">Best for:</span> {m.bestFor}
                  </span>
                </div>
                {m.href ? (
                  <Link
                    to={m.href}
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:underline"
                  >
                    Learn more<span className="sr-only"> about {m.name}</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                ) : null}
              </div>
            </div>
          ))}

        </div>

        {showCta ? (
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="btn-glow btn-lift bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
              <Link to="/contact">
                Get Free Quote <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="btn-lift">
              <Link to="/roofing-materials">See all roofing materials</Link>
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
